import { useEffect, useLayoutEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { selectFacebookPixelIds } from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { BACKEND_ONLY_FACEBOOK_PIXEL_IDS } from 'analytics-constants'

import { useCookieConsentAnswer } from './useCookieConsentAnswer'

const enum FacebookConsent {
  GRANT = 'grant',
  REVOKE = 'revoke',
}

export const useFacebookPixelInitialization = (
  shouldInitialize = true,
): void => {
  const uuid = useSelector(selectUUID)
  const facebookPixelIds = useSelector(selectFacebookPixelIds)
  const isInitializedRef = useRef(false)
  const { canUseAdvertising } = useCookieConsentAnswer()
  const facebookConsent = canUseAdvertising
    ? FacebookConsent.GRANT
    : FacebookConsent.REVOKE

  useEffect(() => {
    if (!shouldInitialize || window.fbq) return

    const script = document.createElement('script')
    script.text = `!function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.disablePushState=!0;
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');`

    document.body.appendChild(script)
  }, [shouldInitialize])

  useLayoutEffect(() => {
    if (!shouldInitialize || !window.fbq) {
      return
    }

    window.fbq('consent', facebookConsent)

    if (
      isInitializedRef.current ||
      !canUseAdvertising ||
      !facebookPixelIds.length ||
      !uuid
    ) {
      return
    }

    const webSafeFacebookPixelIds = facebookPixelIds
      .map((facebookPixelId) => facebookPixelId.trim())
      .filter(
        (facebookPixelId) =>
          !BACKEND_ONLY_FACEBOOK_PIXEL_IDS.includes(facebookPixelId),
      )

    if (!webSafeFacebookPixelIds.length) return

    webSafeFacebookPixelIds.forEach((facebookPixelId) => {
      window.fbq('init', facebookPixelId, { external_id: uuid })
    })

    window.fbq('track', 'PageView')
    isInitializedRef.current = true
  }, [
    canUseAdvertising,
    facebookConsent,
    facebookPixelIds,
    shouldInitialize,
    uuid,
  ])
}
